import {
  Alert,
  Button,
  Grid,
  Heading,
  RadioInput,
  RadioInputGroup,
  Text,
} from "@instructure/ui";
import c3 from "c3";
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useLocation } from "react-router-dom";
import SimpleTable from "../../sections/Cards/SimpleTable/SimpleTable";
import { DashboardService } from "../../services/DashboardService";
import { sort } from "../../utils/utils";
import { Card } from "../Card";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import ProgressStepper from "../ProgressStepper";
import { MULTI, Selector } from "../Selectors/Selector";
import { CourseReviewContext } from "./CourseReview";
import { getCourseReviewSteps } from "./utils";

const CourseResults = () => {
  const showNotification = useNotification();
  const { draftData, setDraftData } = useContext(CourseReviewContext);
  const location = useLocation();
  const { section, relevantTerms } = location.state;

  const [presentationType, setPresentationType] = useState(
    draftData.results.presentation_type
  );
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(draftData.results.data);

  const [appliedFilters, setAppliedFilters] = useState({
    loType: draftData.results.lo_type,
    masteryResults: draftData.results.mastery_results,
    terms: draftData.results.terms,
  });
  const [pendingFilters, setPendingFilters] = useState({
    loType: draftData.results.lo_type,
    masteryResults: draftData.results.mastery_results,
    terms: draftData.results.terms,
  });

  const loInputs = [
    { value: "CSLO", label: "CLO" },
    { value: "PSLO", label: "PLO" },
    { value: "ISLO", label: "ILO" },
  ];

  const presentationInputs = [
    { value: "line", label: t("courseReview.line") },
    { value: "bar", label: t("courseReview.bar") },
    { value: "table", label: t("courseReview.table") },
  ];

  const measuredAt = [
    { value: "true", label: `${t("dashboard.achievedMastery")} %` },
    { value: "false", label: `${t("dashboard.scoreAvg")} %` },
  ];

  // Step 1: Fetch course data
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    async function fetchCourseData() {
      try {
        setLoading(true);
        const termInput =
          appliedFilters.terms.length > 0
            ? appliedFilters.terms
            : relevantTerms;

        const results = await DashboardService.getDashboardResults(
          appliedFilters.loType,
          null,
          "line",
          termInput.map((t) => t.canvas_terms.map((ct) => ct.canvas_id)).flat(),
          [],
          appliedFilters.masteryResults,
          [],
          null,
          [section.offering.parent_course_id],
          ["outcome", "standard"],
          [],
          true,
          signal
        );

        const unroundedResults =
          results["results"]["ac_subject_results_over_time"];
        const roundedResults = unroundedResults.map((row, rowIndex) => {
          if (rowIndex === 0) {
            // If it's the first row (header row), keep it as it is.
            return row;
          } else {
            // For other rows, round numerical values to the hundredth.
            const roundedRow = row.map((value, index) => {
              return index === 0 || value === null
                ? value
                : Number(value.toFixed(2));
            });
            return roundedRow;
          }
        });

        if (roundedResults.length > 1) {
          setResults(roundedResults);
          setDraftData((prev) => ({
            ...prev,
            results: {
              ...prev.results,
              lo_type: appliedFilters.loType,
              mastery_results: appliedFilters.masteryResults,
              terms: appliedFilters.terms,
              data: roundedResults,
            },
          }));
        } else {
          setResults(null);
          setDraftData((prev) => ({
            ...prev,
            results: {
              ...prev.results,
              lo_type: appliedFilters.loType,
              mastery_results: appliedFilters.masteryResults,
              terms: appliedFilters.terms,
              data: null,
            },
          }));
        }
        setLoading(false);
      } catch (e) {
        if (e.name !== "AbortError") {
          setLoading(false);
          showNotification(
            t("notifications.general.fetchError", {
              obj: t("courseReview.courseResults"),
            }),
            ERROR
          );
        }
      }
    }
    fetchCourseData();

    return () => {
      controller.abort();
    };
  }, [appliedFilters, section, relevantTerms, setDraftData, showNotification]);

  // Step 2: Render chart when results or presentationType change
  useEffect(() => {
    if (results) {
      c3.generate({
        bindto: "#assessment-chart",
        data: { x: "terms", columns: results, type: presentationType },
        axis: {
          x: { type: "category" },
          y: {
            label: {
              text:
                appliedFilters.masteryResults === "false"
                  ? t("dashboard.avgAssignmentGrade")
                  : t("dashboard.percentAchievedMastery"),
              position: "outer-middle",
            },
          },
          tick: {
            values: [0, 20, 40, 60, 80, 100],
            format: function (d) {
              return d + "%";
            },
          },
        },
        tooltip: {
          order: function (data1, data2) {
            return sort([data1, data2], (d) => d.name);
          },
        },
      });
    }
    setDraftData((prev) => ({
      ...prev,
      results: { ...prev.results, presentation_type: presentationType },
    }));
  }, [presentationType, results, setDraftData, appliedFilters.masteryResults]);

  function handlePresentationTypeChange(event) {
    setPresentationType(event.target.value);
  }

  function handleLOTypeChange(e) {
    setPendingFilters((prev) => ({ ...prev, loType: e.target.value }));
  }

  function handleResultsTypeChange(e) {
    setPendingFilters((prev) => ({ ...prev, masteryResults: e.target.value }));
  }

  function handleSelectedTermsChange(selectedTerms) {
    const pendingTerms = relevantTerms.filter((term) =>
      selectedTerms.includes(term.id)
    );
    setPendingFilters((prev) => ({ ...prev, terms: pendingTerms }));
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        height: "100%",
      }}
    >
      <ProgressStepper steps={getCourseReviewSteps(t)} currentStep={1} />
      <div
        style={{ overflowY: "auto", margin: "-0.75rem", padding: "0.75rem" }}
      >
        <Card style={{ height: "auto" }}>
          <Heading level="h2" border="bottom" margin="0 0 small 0">
            {t("courseReview.courseResults")}
          </Heading>
          <Grid startAt="x-large">
            <Grid.Row>
              <Grid.Col width={2}>
                <Card style={{ backgroundColor: "#F1F1F1" }}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.75rem",
                    }}
                  >
                    <Heading level="h4" as="h3" border="bottom">
                      {t("results.filters")}
                    </Heading>
                    <RadioInputGroup
                      layout="columns"
                      name="presentationType"
                      value={presentationType}
                      description={
                        <Text size="small">
                          {t("courseReview.presentation")}
                        </Text>
                      }
                      size="small"
                      onChange={handlePresentationTypeChange}
                    >
                      {presentationInputs.map((input) => (
                        <RadioInput
                          key={input.value}
                          value={input.value}
                          label={input.label}
                        />
                      ))}
                    </RadioInputGroup>
                    <RadioInputGroup
                      layout="columns"
                      name="loType"
                      value={pendingFilters.loType}
                      description={
                        <Text size="small">{t("filter.loType")}</Text>
                      }
                      size="small"
                      onChange={handleLOTypeChange}
                    >
                      {loInputs.map((input) => (
                        <RadioInput
                          key={input.value}
                          value={input.value}
                          label={input.label}
                        />
                      ))}
                    </RadioInputGroup>
                    <RadioInputGroup
                      layout="stacked"
                      name="measured"
                      defaultValue={
                        pendingFilters.masteryResults
                          ? pendingFilters.masteryResults
                          : "true"
                      }
                      description={
                        <Text size="small">{t("filter.masteryResults")}</Text>
                      }
                      size="small"
                      onChange={handleResultsTypeChange}
                    >
                      {measuredAt.map((input) => (
                        <RadioInput
                          key={input.value}
                          value={input.value}
                          label={input.label}
                        />
                      ))}
                    </RadioInputGroup>
                    <Selector
                      id="term-selector"
                      size="small"
                      type={MULTI}
                      label={t("terms")}
                      options={relevantTerms.map((term) => ({
                        ...term,
                        label: term.name,
                      }))}
                      selectHandler={handleSelectedTermsChange}
                      selectedOptions={pendingFilters.terms.map((t) => t.id)}
                    />
                    <Button
                      color="primary"
                      onClick={() => setAppliedFilters(pendingFilters)}
                      interaction={
                        JSON.stringify(pendingFilters) !==
                        JSON.stringify(appliedFilters)
                          ? "enabled"
                          : "disabled"
                      }
                    >
                      {t("filter.applyButton")}
                    </Button>
                  </div>
                </Card>
              </Grid.Col>
              <Grid.Col width={10}>
                {loading ? (
                  <Skeleton height={404} borderRadius={5} />
                ) : results ? (
                  presentationType === "table" ? (
                    <SimpleTable
                      data={results}
                      rowTitle={t("results.outcomes")}
                      caption={t("courseReview.tableCaption", {
                        code: section.offering.parent_course_code,
                      })}
                    />
                  ) : (
                    <div id="assessment-chart" />
                  )
                ) : (
                  <Alert>{t("courseReview.noResultsFound")}</Alert>
                )}
              </Grid.Col>
            </Grid.Row>
          </Grid>
        </Card>
      </div>
    </div>
  );
};

export default CourseResults;
